import { Fragment, useEffect, useRef, type MutableRefObject } from 'react';
import type * as React from 'react';

import { useHoverWithDebounce } from '../../hooks';
import { DomainIcon } from '../../icons/DomainIcon';
import { UserTypeIcon } from '../../icons/UserTypeIcon';
import { getIdentityColor } from '../../lib/identity-palette';
import { UserTypeDrillButton, type IdentityKind } from '../../lib/user-type-drill';
import { cn } from '../../lib/utils';

export interface DataContextColumnRow {
  id: string;
  label: string;
  files: number;
  nhi: number;
  hi: number;
  external: number;
  isHighlighted?: boolean;
}

export interface DataContextColumnProps {
  rows: readonly DataContextColumnRow[];
  fadedById?: Readonly<Record<string, boolean>>;
  formatNumber: (value: number) => string;
  onRowHoverEnter?: (id: string, anchorEl: HTMLElement) => void;
  onRowHoverLeave?: (id: string) => void;
  /** V3: click-to-focus. Toggles focus state for the clicked row. */
  onRowClick?: (id: string) => void;
  /** V3: id of the currently focused row (null = none). */
  focusedRowId?: string | null;
  /**
   * When false, rows render without pointer cursor and ignore clicks/hovers.
   * Used in two-axis focus to inert the column opposite the focus origin.
   */
  isInteractive?: boolean;
  rowRefs?: MutableRefObject<Record<string, HTMLDivElement | null>>;
  /**
   * A row's NHI / HI / External count click (TDEV-1116). When supplied, each
   * count is its own button, beside the row's focus hit-area, never inside it.
   */
  onUserTypeClick?: (id: string, kind: IdentityKind) => void;
  /** Rows whose counts stay plain text: no event filter names them, such as a fold ("All Other"). */
  noEventsIds?: ReadonlySet<string>;
  className?: string;
}

const MAX_BAR_HEIGHT = 84;
const MIN_BAR_HEIGHT = 12;

interface DataContextRowItemProps {
  row: DataContextColumnRow;
  index: number;
  barHeight: number;
  isFaded: boolean;
  isFocused: boolean;
  isInteractive: boolean;
  formatNumber: (value: number) => string;
  onHoverEnter?: (id: string, anchorEl: HTMLElement) => void;
  onHoverLeave?: (id: string) => void;
  onRowClick?: (id: string) => void;
  onUserTypeClick?: (kind: IdentityKind) => void;
  rowRef?: (el: HTMLDivElement | null) => void;
}

function DataContextRowItem({
  row,
  index,
  barHeight,
  isFaded,
  isFocused,
  isInteractive,
  formatNumber,
  onHoverEnter,
  onHoverLeave,
  onRowClick,
  onUserTypeClick,
  rowRef,
}: DataContextRowItemProps) {
  const hover = useHoverWithDebounce();
  const identityCount = (kind: IdentityKind, count: React.ReactNode) =>
    onUserTypeClick ? (
      <UserTypeDrillButton
        kind={kind}
        subject={row.label}
        onClick={() => onUserTypeClick(kind)}
        className="gap-1"
      >
        {count}
      </UserTypeDrillButton>
    ) : (
      count
    );
  const color = getIdentityColor(index);
  const barRef = useRef<HTMLSpanElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const onHoverEnterRef = useRef(onHoverEnter);
  onHoverEnterRef.current = onHoverEnter;

  useEffect(() => {
    // Anchor the row's hover popover to the label text: it opens below the
    // label, left-aligned to it, so the row's own counts stay visible.
    if (hover.isHovered && labelRef.current) {
      onHoverEnterRef.current?.(row.id, labelRef.current);
    }
  }, [hover.isHovered, row.id]);

  const handleMouseLeave = () => {
    hover.onMouseLeave();
    onHoverLeave?.(row.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isInteractive) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowClick?.(row.id);
    }
  };

  const cursorClass = isInteractive ? 'cursor-pointer' : 'cursor-default';
  const onMouseEnter = isInteractive ? hover.onMouseEnter : undefined;
  const onMouseLeaveHandler = isInteractive ? handleMouseLeave : undefined;

  return (
    <div
      id={`dc-row-${row.id}`}
      data-testid={`dc-row-${row.id}`}
      data-row-id={row.id}
      ref={rowRef}
      className={cn('flex items-center gap-3 transition-opacity', isFaded && 'opacity-20')}
    >
      <div
        role={isInteractive ? 'button' : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        aria-pressed={isInteractive ? isFocused : undefined}
        aria-label={isInteractive ? row.label : undefined}
        data-row-hit-area="true"
        className="pointer-events-none flex items-center gap-3"
        onClick={isInteractive ? () => onRowClick?.(row.id) : undefined}
        onKeyDown={handleKeyDown}
      >
        <span
          ref={barRef}
          aria-hidden="true"
          data-flow-bar="target"
          className={cn('pointer-events-auto w-2 shrink-0 rounded-[2px]', cursorClass)}
          style={{ backgroundColor: color, height: barHeight }}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeaveHandler}
        />
        <span
          ref={labelRef}
          className={cn(
            'pointer-events-auto w-[120px] shrink-0 text-[12px] font-medium leading-[1.4]',
            cursorClass,
            row.isHighlighted ? 'text-error' : 'text-dawn-black'
          )}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeaveHandler}
        >
          {row.label}
        </span>
      </div>
      <div className="flex items-center gap-5">
        <div className="flex w-[44px] items-center gap-1">
          <DomainIcon name="files" size={16} className="shrink-0" />
          <span className="text-[12px] leading-[1.4] text-dawn-black tabular-nums">
            {formatNumber(row.files)}
          </span>
        </div>
        <div className="flex w-[44px] items-center gap-1">
          {identityCount(
            'NHI',
            <>
              <DomainIcon name="nonHumanIdentity" size={16} className="shrink-0" />
              <span className="text-[12px] leading-[1.4] text-dawn-black tabular-nums">
                {formatNumber(row.nhi)}
              </span>
            </>
          )}
        </div>
        <div className="flex w-[44px] items-center gap-1">
          {identityCount(
            'HI',
            <>
              <DomainIcon name="humanIdentity" size={16} className="shrink-0" />
              <span className="text-[12px] leading-[1.4] text-dawn-black tabular-nums">
                {formatNumber(row.hi)}
              </span>
            </>
          )}
        </div>
        <div className="flex w-[44px] items-center gap-1">
          {row.external > 0 &&
            identityCount(
              'External',
              <>
                {/* External glyph swapped from inline `<User className="text-error" />` (lucide,
                  14px) to the canonical `UserTypeIcon` atom at 18px per Figma `289:49570` and
                  design.md § 3.9. */}
                <UserTypeIcon type="external" className="shrink-0 text-error" />
                <span className="text-[12px] leading-[1.4] text-error tabular-nums">
                  {formatNumber(row.external)}
                </span>
              </>
            )}
        </div>
      </div>
    </div>
  );
}

export function DataContextColumn({
  rows,
  fadedById,
  formatNumber,
  onRowHoverEnter,
  onRowHoverLeave,
  onRowClick,
  focusedRowId,
  isInteractive = true,
  rowRefs,
  onUserTypeClick,
  noEventsIds,
  className,
}: DataContextColumnProps) {
  const maxFiles = Math.max(...rows.map((r) => r.files), 1);

  return (
    <div
      data-testid="data-context-column"
      className={cn('flex h-full flex-col items-stretch justify-between', className)}
    >
      {rows.map((row, index) => {
        const barHeight = Math.max(
          MIN_BAR_HEIGHT,
          Math.round((row.files / maxFiles) * MAX_BAR_HEIGHT)
        );
        return (
          <Fragment key={row.id}>
            {index > 0 && <div className="h-px bg-grey-100" aria-hidden="true" />}
            <DataContextRowItem
              row={row}
              index={index}
              barHeight={barHeight}
              isFaded={fadedById?.[row.id] === true}
              isFocused={focusedRowId === row.id}
              isInteractive={isInteractive}
              formatNumber={formatNumber}
              onHoverEnter={onRowHoverEnter}
              onHoverLeave={onRowHoverLeave}
              onRowClick={onRowClick}
              onUserTypeClick={
                onUserTypeClick && noEventsIds?.has(row.id) !== true
                  ? (kind) => onUserTypeClick(row.id, kind)
                  : undefined
              }
              rowRef={
                rowRefs
                  ? (el) => {
                      rowRefs.current[row.id] = el;
                    }
                  : undefined
              }
            />
          </Fragment>
        );
      })}
    </div>
  );
}

DataContextColumn.displayName = 'DataContextColumn';
